import { DeleteOutlined, EditOutlined, HolderOutlined, InboxOutlined } from '@ant-design/icons'
import { Button, Modal, Space, Switch, Table, Tooltip } from 'antd'
import { WhatsNewItem } from 'Modules'
import React, { useState } from 'react'
import { toast } from 'react-toastify'

import ProtectedButton from 'component/button/protected-button'
import { FEATURE } from 'constants/feature'
import {
  useDeleteWhatsNew,
  useReorderWhatsNew,
  useUpdateWhatsNewStatus,
  useWhatsNewList,
} from 'hooks/whats-new'
import { formatDateWithPattern } from 'utils/format-time'

import style from './whats-new-table.module.scss'

interface WhatsNewTableProps {
  onEdit: (item: WhatsNewItem) => void
  onRefresh: () => void
  onCreate: () => void
}

export const WhatsNewTable: React.FC<WhatsNewTableProps> = ({ onEdit, onRefresh, onCreate }) => {
  const { items, total, isLoading } = useWhatsNewList()
  const [draggedItem, setDraggedItem] = useState<string | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const [confirm, setConfirm] = useState<{
    type: 'status' | 'delete'
    item: WhatsNewItem
  } | null>(null)

  const deleteWhatsNew = useDeleteWhatsNew()
  const updateStatus = useUpdateWhatsNewStatus()
  const reorder = useReorderWhatsNew()

  const handleDeleteItem = async (id: string) => {
    try {
      await deleteWhatsNew.mutateAsync(id)
      toast.success('Delete item successfully')
      setConfirm(null)
      onRefresh()
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to delete item'
      toast.error(message)
    }
  }

  const handleTogglePublish = async (item: WhatsNewItem) => {
    const newStatus = item.status === 'PUBLISHED' ? 'UNPUBLISHED' : 'PUBLISHED'
    try {
      await updateStatus.mutateAsync({ id: item._id, payload: { status: newStatus } })
      const message =
        newStatus === 'PUBLISHED' ? 'Publish item successfully' : 'Unpublish item successfully'
      toast.success(message)
      setConfirm(null)
      onRefresh()
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to update status'
      toast.error(message)
    }
  }

  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedItem(id)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    setDragOverIndex(index)
  }

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    if (!draggedItem || draggedItem === items[dropIndex]?._id) {
      setDraggedItem(null)
      setDragOverIndex(null)
      return
    }

    const dragIndex = items.findIndex((item) => item._id === draggedItem)
    if (dragIndex === -1) return

    const newItems = [...items]
    const [removed] = newItems.splice(dragIndex, 1)
    newItems.splice(dropIndex, 0, removed)

    try {
      const ids = newItems.map((item) => item._id)
      await reorder.mutateAsync({ ids })
      toast.success('Success')
      onRefresh()
    } catch (error: any) {
      const errorCode = error?.response?.data?.data?.error
      if (errorCode === 'WHATS_NEW_LIST_CHANGED') {
        toast.error('List changed by another admin. Please refresh and try again.')
      } else {
        const message = error?.response?.data?.message || 'Failed to reorder'
        toast.error(message)
      }
      onRefresh()
    } finally {
      setDraggedItem(null)
      setDragOverIndex(null)
    }
  }

  const columns = [
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
      width: 100,
      render: (_: any, __: any, index: number) => (
        <div
          draggable
          onDragStart={(e) => handleDragStart(e, items[index]._id)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDrop={(e) => handleDrop(e, index)}
          className={`${style.dragHandle} ${dragOverIndex === index ? style.dragOver : ''}`}
        >
          <Tooltip title="Drag to reorder">
            <HolderOutlined />
          </Tooltip>
          <span>{items[index]?.order}</span>
        </div>
      ),
    },
    {
      title: 'Media',
      key: 'media',
      width: 130,
      render: (_: any, record: WhatsNewItem) => {
        const { type, url, fileName } = record.media
        return (
          <div className={style.thumbnail}>
            {type === 'MP4' ? (
              <video src={url} muted preload="metadata" />
            ) : (
              <img src={url} alt={fileName} />
            )}
            <span className={style.badge}>{type === 'MP4' ? 'VIDEO' : type}</span>
          </div>
        )
      },
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: { showTitle: false },
      width: 280,
      render: (title: string) => (
        <Tooltip title={title}>
          <span>{title}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (status: string, record: WhatsNewItem) => {
        const published = status === 'PUBLISHED'
        return (
          <div className={style.status}>
            <Switch
              checked={published}
              onClick={() => setConfirm({ type: 'status', item: record })}
            />
            <span className={published ? '' : style.unpublished}>
              {published ? 'Published' : 'Unpublished'}
            </span>
          </div>
        )
      },
    },
    {
      title: 'Updated date',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (date: string) => formatDateWithPattern(date, 'DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: WhatsNewItem) => (
        <Space size="small">
          <Button type="text" icon={<EditOutlined />} onClick={() => onEdit(record)} title="Edit" />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => setConfirm({ type: 'delete', item: record })}
            title="Delete"
          />
        </Space>
      ),
    },
  ]

  const isPublishing = confirm?.type === 'status' && confirm.item.status !== 'PUBLISHED'

  return (
    <>
      {total === 0 && !isLoading ? (
        <div className={style.emptyState}>
          <InboxOutlined style={{ fontSize: 48, color: 'rgba(0, 0, 0, 0.25)' }} />
          <p className={style.emptyTitle}>No items yet</p>
          <p className={style.emptyDescription}>
            Create an announcement to show users in the What&apos;s New popup after they connect
            their wallet.
          </p>
          <ProtectedButton type="primary" onClick={onCreate} feature={FEATURE.WHATS_NEW_CREATE}>
            Create Item
          </ProtectedButton>
        </div>
      ) : (
        <>
          <Table
            columns={columns}
            dataSource={items}
            rowKey="_id"
            loading={isLoading}
            pagination={false}
            scroll={{ x: 1000 }}
          />
        </>
      )}

      <Modal
        open={!!confirm}
        title={
          confirm?.type === 'delete'
            ? 'Delete item'
            : isPublishing
              ? 'Publish item'
              : 'Unpublish item'
        }
        onCancel={() => setConfirm(null)}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => setConfirm(null)}>
            Cancel
          </Button>,
          <Button
            key="ok"
            type="primary"
            danger={confirm?.type === 'delete'}
            loading={deleteWhatsNew.isPending || updateStatus.isPending}
            onClick={() =>
              confirm &&
              (confirm.type === 'delete'
                ? handleDeleteItem(confirm.item._id)
                : handleTogglePublish(confirm.item))
            }
          >
            {confirm?.type === 'delete' ? 'Delete' : isPublishing ? 'Publish' : 'Unpublish'}
          </Button>,
        ]}
      >
        {confirm?.type === 'delete'
          ? 'Are you sure you want to delete this item? This action cannot be undone.'
          : isPublishing
            ? 'Publish this item? It will be shown to users who have not seen it yet.'
            : 'Unpublish this item? It will no longer be shown to users.'}
      </Modal>
    </>
  )
}
